/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import {
	AfterContentInit, ChangeDetectionStrategy, Component, ContentChildren, EventEmitter, Input, OnChanges, Output,
	QueryList, SimpleChanges, TemplateRef,
} from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { ORBIT_PAGINATOR_SELECT_CONFIG } from './orbit-paginator.config';
import {
	OrbitTableActionConfig, OrbitTableColumnConfig, OrbitTableConfig, OrbitTableListActionEvent, OrbitTablePageEvent,
	OrbitTableRowActionEvent, OrbitTableRowDropEvent, OrbitTableSelectionEvent, OrbitTableSortEvent,
} from './orbit-table.models';
import { OrbitTableCellTemplateDirective, OrbitTableHeaderTemplateDirective } from './orbit-table-template.directive';
import { OrbitTableService } from './orbit-table.service';

@Component({
	selector: 'orbit-table',
	templateUrl: './orbit-table.component.html',
	styleUrls: ['./orbit-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [OrbitTableService],
	standalone: false,
})
export class OrbitTableComponent<T> implements AfterContentInit, OnChanges {
	@Input({ required: true }) config!: OrbitTableConfig<T>;
	@Input() rows: readonly T[] = [];
	@Input() resultCount: number | null = null;
	@Input() totalCount: number | null = null;
	@Input() loading = false;
	@Input() error = false;
	@Input() selectedRowId: string | number | null = null;
	@Input() filterTemplate: TemplateRef<unknown> | null = null;

	@Output() selectedRowChange = new EventEmitter<OrbitTableSelectionEvent<T>>();
	@Output() rowClick = new EventEmitter<T>();
	@Output() rowDoubleClick = new EventEmitter<T>();
	@Output() rowAction = new EventEmitter<OrbitTableRowActionEvent<T>>();
	@Output() listAction = new EventEmitter<OrbitTableListActionEvent>();
	@Output() sortChange = new EventEmitter<OrbitTableSortEvent>();
	@Output() pageChange = new EventEmitter<OrbitTablePageEvent>();
	@Output() rowDrop = new EventEmitter<OrbitTableRowDropEvent<T>>();

	@ContentChildren(OrbitTableCellTemplateDirective) cellTemplates!: QueryList<OrbitTableCellTemplateDirective>;
	@ContentChildren(OrbitTableHeaderTemplateDirective) headerTemplates!: QueryList<OrbitTableHeaderTemplateDirective>;

	private cellTemplateMap = new Map<string, OrbitTableCellTemplateDirective>();
	private headerTemplateMap = new Map<string, OrbitTableHeaderTemplateDirective>();
	readonly paginatorSelectConfig = ORBIT_PAGINATOR_SELECT_CONFIG;

	constructor(private readonly tableService: OrbitTableService<T>) {}

	ngAfterContentInit(): void {
		this.rebuildTemplateMaps();
		this.cellTemplates.changes.subscribe(() => this.rebuildTemplateMaps());
		this.headerTemplates.changes.subscribe(() => this.rebuildTemplateMaps());
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.config) {
			this.tableService.setConfig(this.config, changes.config.firstChange);
		}
		if (changes.rows) this.tableService.setRows(this.rows);
	}

	get displayedColumns(): string[] {
		return [...(this.config?.columns ?? []).map(column => column.key), ...(this.config?.rowActions?.length ? ['__actions'] : [])];
	}

	get displayedRows(): readonly T[] {
		return this.tableService.displayedRows;
	}
	get showFooter(): boolean { return !!this.config?.footer?.enabled && this.displayedRows.length > 0; }

	get pageIndex(): number { return this.tableService.pageIndex; }
	get pageSize(): number { return this.tableService.pageSize; }
	get sortActive(): string { return this.tableService.sortActive; }
	get sortDirection(): SortDirection { return this.tableService.sortDirection; }
	get paginatorLength(): number { return this.config?.pagination?.mode === 'server' ? (this.resultCount ?? this.rows.length) : this.rows.length; }
	get currentResultCount(): number { return this.resultCount ?? this.rows.length; }
	get actionsColumnWidth(): string {
		const actionCount = this.config?.rowActions?.length ?? 0;
		if (!actionCount) return '0px';
		const actionButtonWidth = 32;
		const actionGap = 4;
		const horizontalPadding = 48;
		const width = (actionCount * actionButtonWidth) + ((actionCount - 1) * actionGap) + horizontalPadding;
		return `${width}px`;
	}

	get resultEntityLabel(): string {
		const summary = this.config?.resultSummary;
		if (!summary) return '';
		return this.currentResultCount === 1 ? summary.businessObjectName : summary.businessObjectNamePlural;
	}

	columnValue(column: OrbitTableColumnConfig<T>, row: T): unknown {
		return this.tableService.columnValue(column, row);
	}

	displayValue(column: OrbitTableColumnConfig<T>, row: T): string {
		const value = this.columnValue(column, row);
		if (column.format) return column.format(value, row);
		return value == null ? '' : String(value);
	}

	cellTemplate(columnKey: string) { return this.cellTemplateMap.get(columnKey)?.template ?? null; }
	headerTemplate(columnKey: string) { return this.headerTemplateMap.get(columnKey)?.template ?? null; }

	isSortable(column: OrbitTableColumnConfig<T>): boolean { return !!this.config?.sorting?.enabled && column.sortable !== false; }
	isSticky(column: OrbitTableColumnConfig<T>, index: number): boolean {
		return !!column.sticky || (this.config?.scroll?.stickyFirstColumn !== false && index === 0);
	}

	footerValue(column: OrbitTableColumnConfig<T>): unknown {
		return typeof column.footerValue === 'function' ? column.footerValue(this.rows) : column.footerValue;
	}

	footerDisplayValue(column: OrbitTableColumnConfig<T>): string {
		const value = this.footerValue(column);
		if (column.footerFormat) return column.footerFormat(value, this.rows);
		if (column.format) return column.format(value, this.rows[0] as T);
		return value == null ? '' : String(value);
	}

	onSortChanged(sort: Sort): void {
		this.tableService.applySort(sort);
		this.sortChange.emit(sort);
	}

	onPageChanged(page: PageEvent): void {
		this.tableService.applyPage(page);
		this.pageChange.emit(page);
	}

	onRowDropped(event: CdkDragDrop<readonly T[]>): void {
		if (!this.config?.reordering?.enabled || event.previousIndex === event.currentIndex) return;
		this.rowDrop.emit({ previousIndex: event.previousIndex, currentIndex: event.currentIndex, row: event.item.data as T });
	}

	onRowClicked(row: T, event: MouseEvent): void {
		if (this.isInteractiveEvent(event)) return;
		this.rowClick.emit(row);
		if (this.config?.selection?.mode === 'single' && this.config.selection.selectOnRowClick !== false) this.selectRow(row);
	}

	onRowDoubleClicked(row: T, event: MouseEvent): void {
		if (!this.isInteractiveEvent(event)) this.rowDoubleClick.emit(row);
	}

	onRowKeydown(row: T, event: KeyboardEvent): void {
		if (this.config?.selection?.mode !== 'single' || this.isInteractiveEvent(event) || (event.key !== 'Enter' && event.key !== ' ')) return;
		event.preventDefault();
		this.selectRow(row);
	}

	selectRow(row: T): void {
		const rowId = this.rowId(row);
		this.selectedRowId = rowId;
		this.selectedRowChange.emit({ row, rowId });
	}

	isSelected(row: T): boolean { return this.config?.selection?.mode === 'single' && this.selectedRowId === this.rowId(row); }
	isSelectable(): boolean { return this.config?.selection?.mode === 'single'; }
	rowId(row: T): string | number { return this.config.selection?.rowId(row) ?? String(this.rows.indexOf(row)); }

	invokeRowAction(action: OrbitTableActionConfig<T>, row: T, event: Event): void {
		event.stopPropagation();
		if (!this.actionDisabled(action, row)) this.rowAction.emit({ actionId: action.id, row });
	}

	invokeListAction(action: OrbitTableActionConfig<void>, event: Event): void {
		event.stopPropagation();
		if (!this.actionDisabled(action, undefined)) this.listAction.emit({ actionId: action.id });
	}

	actionVisible(action: OrbitTableActionConfig<any>, row?: T): boolean {
		return typeof action.visible === 'function' ? action.visible(row) : action.visible !== false;
	}

	actionDisabled(action: OrbitTableActionConfig<any>, row?: T): boolean {
		return typeof action.disabled === 'function' ? action.disabled(row) : action.disabled === true;
	}

	actionAriaLabel(action: OrbitTableActionConfig<any>, row?: T): string {
		return typeof action.ariaLabel === 'function' ? action.ariaLabel(row) : action.ariaLabel ?? action.label;
	}

	actionTooltip(action: OrbitTableActionConfig<any>, row?: T): string {
		return typeof action.tooltip === 'function' ? action.tooltip(row) : action.tooltip ?? action.label;
	}

	tooltipFor(column: OrbitTableColumnConfig<T>, row: T): string {
		if (typeof column.tooltip === 'function') return column.tooltip(row) ?? '';
		return column.tooltip ? this.displayValue(column, row) : '';
	}

	cellClass(column: OrbitTableColumnConfig<T>, row: T): string {
		return typeof column.cellClass === 'function' ? column.cellClass(row) : column.cellClass ?? '';
	}

	rowClasses(row: T): Record<string, boolean> {
		const configured = typeof this.config.rowClass === 'function' ? this.config.rowClass(row) : this.config.rowClass ?? '';
		return {
			'orbit-table__row': true,
			'orbit-table__row--hoverable': this.config.rowHover !== false,
			'orbit-table__row--selectable': this.isSelectable(),
			'orbit-table__row--selected': this.isSelected(row),
			'orbit-table__row--selected-actions-visible': this.isSelected(row) && this.config.showRowActionsOnSelected !== false,
			[configured]: !!configured,
		};
	}

	trackByColumn(_: number, column: OrbitTableColumnConfig<T>): string { return column.key; }
	trackByRow = (index: number, row: T): string | number => this.config?.selection?.rowId ? this.rowId(row) : index;
	trackByAction(_: number, action: OrbitTableActionConfig<any>): string { return action.id; }

	private rebuildTemplateMaps(): void {
		this.cellTemplateMap = new Map((this.cellTemplates?.toArray() ?? []).map(item => [item.columnKey, item]));
		this.headerTemplateMap = new Map((this.headerTemplates?.toArray() ?? []).map(item => [item.columnKey, item]));
	}

	private isInteractiveEvent(event: Event): boolean {
		const target = event.target as HTMLElement | null;
		return !!target?.closest('button, a, input, select, textarea, [role="button"], [contenteditable="true"], [data-orbit-table-interactive]');
	}
}
